import { DidUser } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { PlanGroup } from '@d-id/types';
import { useFirstUserSubscriptionTimestamp, useUnlimitedFirstMonthCreditsEnabled } from './useUnlimitedFirstMonthCreditsEnabled';

interface UnlimitedVideosData {
    days: number;
    startTimestamp: number;
    endTimestamp?: number;
}

const MS_TO_DAYS = 1000 * 60 * 60 * 24;

function useOldUnlimitedVideosEnabled(user?: DidUser | null) {
    const unlimitedVideosData = useFlag<UnlimitedVideosData>('unlimited_videos_data', {
        days: 0,
        startTimestamp: 0,
        endTimestamp: Infinity,
    });
    const { timestamp: userFirstSubscriptionTimestamp, isFetched } = useFirstUserSubscriptionTimestamp();

    let unlimitedActive = false;

    if (unlimitedVideosData.days !== 0) {
        const unlimitedGlobalPeriodStartTimestamp = new Date(unlimitedVideosData.startTimestamp).getTime() || 0;
        const globalEndTimestamp = unlimitedVideosData.endTimestamp ? new Date(unlimitedVideosData.endTimestamp).getTime() : Infinity;

        // If user's first subscription is after endTimestamp, they should be charged
        if (userFirstSubscriptionTimestamp > globalEndTimestamp && globalEndTimestamp !== Infinity) {
            return {
                enabled: false,
                isFetched,
            };
        }

        const unlimitedIndividualExpiryTimestamp =
            Math.max(userFirstSubscriptionTimestamp, unlimitedGlobalPeriodStartTimestamp) + unlimitedVideosData.days * MS_TO_DAYS;

        unlimitedActive = new Date().getTime() < unlimitedIndividualExpiryTimestamp;
    }
    return {
        enabled:
            user?.planGroup &&
            ![PlanGroup.TRIAL, PlanGroup.ENTERPRISE, PlanGroup.ENTERPRISE_TRIAL, PlanGroup.FREE].includes(user?.planGroup as PlanGroup) &&
            unlimitedActive,
        isFetched,
    };
}

export function useUnlimitedVideosEnabled(user?: DidUser | null) {
    const { enabled: unlimitedFirstMonthCreditsEnabled, isFetched: unlimitedFirstMonthCreditsIsFetched } =
        useUnlimitedFirstMonthCreditsEnabled(user);
    const { enabled: oldUnlimitedVideosEnabled, isFetched: oldUnlimitedVideosIsFetched } = useOldUnlimitedVideosEnabled(user);

    return {
        enabled: unlimitedFirstMonthCreditsEnabled || oldUnlimitedVideosEnabled,
        isFetched: unlimitedFirstMonthCreditsIsFetched || oldUnlimitedVideosIsFetched,
    };
}
